import { forwardRef, type InputHTMLAttributes, useId } from 'react';

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  /** Validation or hint message; when `invalid` is set it is announced as an error. */
  message?: string | undefined;
  invalid?: boolean;
}

/** Labelled, accessible text input with error wiring. */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, message, invalid = false, required, className, ...rest },
  ref,
) {
  const inputId = useId();
  const messageId = `${inputId}-message`;

  return (
    <div className="zs-field">
      <label className="zs-field__label" htmlFor={inputId}>
        {label}
        {required ? <span aria-hidden="true"> *</span> : null}
      </label>
      <input
        id={inputId}
        ref={ref}
        className={['zs-field__input', className].filter(Boolean).join(' ')}
        aria-invalid={invalid || undefined}
        aria-describedby={message ? messageId : undefined}
        aria-required={required || undefined}
        required={required}
        {...rest}
      />
      {message ? (
        <span
          id={messageId}
          className={invalid ? 'zs-field__error' : 'zs-field__label'}
          role={invalid ? 'alert' : undefined}
        >
          {message}
        </span>
      ) : null}
    </div>
  );
});
